import type {
  FormDefinitionResponse,
  FormFieldValueSimpleResponse,
} from 'src/api/ProactApiClient';

const QUALIFIED_NAME_DELIMITER = '::';

export interface SplitQualifiedFieldName {
  hostInvariantId?: string;
  innerInvariantId: string;
}

export const buildQualifiedFieldName = (
  hostInvariantId: string,
  innerInvariantId: string
): string => `${hostInvariantId}${QUALIFIED_NAME_DELIMITER}${innerInvariantId}`;

export const splitQualifiedFieldName = (
  name: string
): SplitQualifiedFieldName => {
  const delimiterIndex = name.indexOf(QUALIFIED_NAME_DELIMITER);
  if (delimiterIndex === -1) {
    return {innerInvariantId: name};
  }
  return {
    hostInvariantId: name.slice(0, delimiterIndex),
    innerInvariantId: name.slice(
      delimiterIndex + QUALIFIED_NAME_DELIMITER.length
    ),
  };
};

export const normalizeFormDefinitionSubFormFieldIds = (
  response: FormDefinitionResponse
): FormDefinitionResponse => ({
  ...response,
  formFields: response.formFields.map(field => ({
    ...field,
    subFormFieldId: field.subFormFieldId ?? undefined,
    subFormFieldInvariantId: field.subFormFieldInvariantId ?? undefined,
  })),
});

export const normalizeFormFieldValues = (
  fieldValues?: FormFieldValueSimpleResponse[]
): FormFieldValueSimpleResponse[] | undefined =>
  fieldValues?.map(fieldValue => ({
    ...fieldValue,
    subFormFieldId: fieldValue.subFormFieldId ?? undefined,
  }));

export const resolveQualifiedFieldValueName = (
  fieldValue: FormFieldValueSimpleResponse,
  formDefinition: FormDefinitionResponse
): string => {
  const innerInvariantId = fieldValue.formField.invariantId!;
  if (fieldValue.subFormFieldId === undefined) {
    return innerInvariantId;
  }

  const hostField = formDefinition.formFields.find(
    field => field.id === fieldValue.subFormFieldId
  );
  return hostField?.invariantId
    ? buildQualifiedFieldName(hostField.invariantId, innerInvariantId)
    : innerInvariantId;
};

export interface DedupableDefinitionField {
  invariantId?: string;
  subFormFieldInvariantId?: string;
  label: string;
}

export const dedupeToDefinitionFields = <T extends DedupableDefinitionField>(
  fields: T[],
  allDefinitionFields: DedupableDefinitionField[] = fields
): T[] => {
  const occurrenceCounts = new Map<string, number>();
  const hostLabelsByInvariantId = new Map<string, string[]>();
  fields.forEach(field => {
    if (!field.invariantId) return;

    occurrenceCounts.set(
      field.invariantId,
      (occurrenceCounts.get(field.invariantId) ?? 0) + 1
    );

    const hostLabel = field.subFormFieldInvariantId
      ? allDefinitionFields.find(
          f => f.invariantId === field.subFormFieldInvariantId
        )?.label
      : undefined;

    const hostLabels = hostLabelsByInvariantId.get(field.invariantId) ?? [];
    if (hostLabel && !hostLabels.includes(hostLabel)) hostLabels.push(hostLabel);
    hostLabelsByInvariantId.set(field.invariantId, hostLabels);
  });

  const seen = new Set<string>();
  const result: T[] = [];
  fields.forEach(field => {
    if (!field.invariantId || seen.has(field.invariantId)) return;
    seen.add(field.invariantId);

    const hostLabels = hostLabelsByInvariantId.get(field.invariantId)!;
    const collapsed = (occurrenceCounts.get(field.invariantId) ?? 0) > 1;
    result.push(
      collapsed && hostLabels.length > 0
        ? {...field, label: `${field.label} (any ${hostLabels.join(', ')})`}
        : field
    );
  });

  return result;
};
